import { JWT_KEY } from 'shared/constants';

/**
 * @param {string} value 입력값 공백 제거
 * @param {string} type ENG: 영문만, NUM: 숫자만
 * @returns {string}
 */
export const removeWhitespace = (value: string, type?: 'ENG' | 'NUM') => {
  if (type === 'ENG') {
    return value.replace(/[^a-zA-Z]/g, '');
  } else if (type === 'NUM') {
    return value.replace(/[^0-9]/g, '');
  } else {
    return value.replace(/\s/g, '');
  }
};

/**
 * @param {string} value 영문만 입력받고 대문자로 변환
 * @returns {string}
 */
export const toUpperCaseEnglishOnly = (value: string) => {
  return value.replace(/[^a-zA-Z]/g, '').toUpperCase();
};

/**
 * @param {string} value 숫자만 입력받음
 * @returns {string}
 */
export const numbersOnly = (value: string) => {
  return value.replace(/[^0-9]/g, '');
};

/**
 * @param {string} email 이메일 정규식 유효성 검사
 * @returns {boolean}
 */
export const isValidEmail = (email: string) => {
  return email.match(/^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,4}$/i) !== null;
};

/**
 * @param {string} password 비밀번호 정규식 유효성 검사
 * @returns {boolean}
 */
export const isValidPassword = (password: string): boolean => {
  // 정규식 조건:
  // - 최소 8글자 이상
  // - 소문자 하나 이상
  // - 대문자 하나 이상
  // - 숫자 하나 이상 포함
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
  return regex.test(password); // `.test`는 일치 여부를 반환
};

/**
 * @param {string} phoneNumber 전화번호 정규식 유효성 검사
 * @returns {boolean}
 */
export const isValidPhoneNumber = (phoneNumber: string) => {
  return phoneNumber.match(/^01(?:0|1|[6-9])-(?:\d{3}|\d{4})-\d{4}$/);
};

/**
 * @param {string} input 전화번호 형식으로 정규식 변환
 * @returns {string|undefined}
 */
export const formatPhoneNumber = (input?: string) => {
  return input
    ?.replace(/[^0-9]/g, '')
    .replace(/^(\d{0,3})(\d{0,4})(\d{0,4})$/g, '$1-$2-$3')
    .replace(/(-{1,2})$/g, '');
};

export const getJwt = () => window.localStorage.getItem(JWT_KEY) ?? '';

export const saveJwt = (jwt: string) => {
  window.localStorage.setItem(JWT_KEY, jwt);
};

export const clearJwt = () => {
  window.localStorage.removeItem(JWT_KEY ?? '');
};

// 브라우저 내장 Web Crypto 사용 (crypto-js 대체 — 엔트리 번들 경량화).
// 출력은 기존 crypto-js와 동일한 hex 문자열이라 GTM user_id 값이 그대로 유지된다.
export const sha256 = async (message: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(message));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * @description
 * 현재 window.origin 값을 기반으로 서버 타입을 반환합니다.
 * - 로컬 개발 환경: 'LOCAL'
 * - 개발 서버 환경: 'TEST'
 * - 그 외(운영 등): 'PRODUCTION'
 *
 * @returns {'LOCAL' | 'TEST' | 'PRODUCTION'} 서버 타입 문자열
 *
 * @example
 * const serverType = getServerType();
 * // 'LOCAL', 'TEST', 'PRODUCTION' 중 하나 반환
 */
export const getServerType = () => {
  if (window.origin.startsWith('http://localhost:3000')) {
    return 'LOCAL';
  }

  if (window.origin.startsWith('https://dev')) {
    return 'TEST';
  }

  return 'PRODUCTION';
};

/**
 * @description
 * 카드 종류가 MASTERCARD 또는 VISA인지 확인하는 함수
 * @param cardKind 카드 종류
 * @returns {boolean}
 */
export const isKnownCard = (cardKind: string): boolean => {
  return ['MASTERCARD', 'VISA'].includes(cardKind);
};
